import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Euro, Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatMonthLabel = (key) => {
  const [y, m] = key.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (key, delta) => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todas (sin canceladas)' },
  { value: 'CONFIRMED', label: 'Confirmadas' },
  { value: 'COMPLETED', label: 'Completadas' },
  { value: 'CANCELLED', label: 'Canceladas' },
];

const ReservationsMonthlyWidget = () => {
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey());
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fromMonth, setFromMonth] = useState(shiftMonth(currentMonthKey(), -5));
  const [toMonth, setToMonth] = useState(currentMonthKey());
  const [statusFilter, setStatusFilter] = useState('ALL');

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const res = await adminApi.exportMonthlyReport({
        format: fmt,
        from_month: fromMonth || undefined,
        to_month: toMonth || undefined,
        status_filter: statusFilter,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resumen_mensual_cg_valet_parking.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Informe ${fmt.toUpperCase()} descargado`);
    } catch {
      toast.error('Error al generar el informe');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    adminApi.getReservationsByMonth()
      .then((res) => setMonths(res.data.months || []))
      .catch(() => toast.error('No se pudo cargar el resumen mensual'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const selected = months.find((m) => m.month === selectedMonth) || { count: 0, total_euros: 0 };

  // Next 2 months + current + last 5 months, most recent first
  const recentKeys = Array.from({ length: 8 }, (_, i) => shiftMonth(currentMonthKey(), 2 - i));
  const recentMonths = recentKeys.map((key) => {
    const found = months.find((m) => m.month === key);
    return { month: key, count: found?.count || 0, total_euros: found?.total_euros || 0 };
  });

  return (
    <div className="card-dark p-4 mb-6" data-testid="monthly-reservations-widget">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Month selector */}
        <div className="flex items-center gap-3 lg:min-w-[340px]">
          <button
            onClick={() => setSelectedMonth(shiftMonth(selectedMonth, -1))}
            className="p-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
            data-testid="monthly-widget-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center">
            <p className="text-sm font-semibold text-white capitalize" data-testid="monthly-widget-label">
              {formatMonthLabel(selectedMonth)}
            </p>
            <div className="flex items-center justify-center gap-4 mt-1">
              <span className="flex items-center gap-1.5 text-zinc-300 text-sm">
                <CalendarDays className="w-3.5 h-3.5 text-primary" />
                <span className="font-bold text-white text-lg" data-testid="monthly-widget-count">{selected.count}</span>
                reservas
              </span>
              <span className="flex items-center gap-1.5 text-zinc-300 text-sm">
                <Euro className="w-3.5 h-3.5 text-primary" />
                <span className="font-bold text-primary text-lg" data-testid="monthly-widget-total">
                  {selected.total_euros.toFixed(2)}€
                </span>
              </span>
            </div>
          </div>
          <button
            onClick={() => setSelectedMonth(shiftMonth(selectedMonth, 1))}
            className="p-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
            data-testid="monthly-widget-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Divider */}
        <div className="hidden lg:block w-px h-14 bg-white/10" />
        <div className="lg:hidden border-t border-white/10" />

        {/* Recent months cards */}
        <div className="flex-1 grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="monthly-widget-recent">
          {recentMonths.map((m) => {
            const isFuture = m.month > currentMonthKey();
            return (
            <button
              key={m.month}
              onClick={() => setSelectedMonth(m.month)}
              className={`rounded-lg px-2 py-2 text-center transition-colors border ${
                m.month === selectedMonth
                  ? 'bg-primary/10 border-primary/50'
                  : isFuture
                    ? 'bg-zinc-800/30 border-dashed border-zinc-700 hover:bg-zinc-800'
                    : 'bg-zinc-800/60 border-transparent hover:bg-zinc-800'
              }`}
              data-testid={`monthly-card-${m.month}`}
            >
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 capitalize">
                {formatMonthLabel(m.month).slice(0, 3)} {m.month.split('-')[0].slice(2)}
              </p>
              <p className="text-sm font-bold text-white">{m.count}</p>
              <p className="text-[11px] text-primary font-medium">{m.total_euros.toFixed(0)}€</p>
            </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <p className="text-[11px] text-zinc-500">
          Agrupado por fecha de entrada · No incluye reservas canceladas
        </p>
        <button
          onClick={() => setShowExport(!showExport)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
          data-testid="monthly-export-toggle"
        >
          <Download className="w-3.5 h-3.5" />
          Exportar informe
        </button>
      </div>

      {/* Export panel */}
      {showExport && (
        <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-end gap-3" data-testid="monthly-export-panel">
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Desde (mes)</label>
            <input
              type="month"
              value={fromMonth}
              onChange={(e) => setFromMonth(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1.5 text-sm text-white"
              data-testid="export-from-month"
            />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Hasta (mes)</label>
            <input
              type="month"
              value={toMonth}
              onChange={(e) => setToMonth(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-md px-2 py-1.5 text-sm text-white"
              data-testid="export-to-month"
            />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-md px-2 py-[7px] text-sm text-white"
              data-testid="export-status-filter"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('xlsx')}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-black text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="export-xlsx-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Excel
            </button>
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-700 text-white text-sm font-semibold hover:bg-zinc-600 transition-colors disabled:opacity-50"
              data-testid="export-csv-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationsMonthlyWidget;
